import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import SelectTranslation from "./SelectTranslation";
import { useExportServicePack } from "../../hooks/translation/useExportServicePack";
import { ExportServicePackTranslationForm } from "../../api/exportServicePackTranslation";
import Icon from "../../components/ui/Icon";
import DatePicker from "react-datepicker";
import { Controller, useForm } from "react-hook-form";
import "react-datepicker/dist/react-datepicker.css";

const schema = yup.object({
  destinationLanguage: yup.string().default("en").required("Language selection is required"),
  sylobProduct: yup.string().required("Product selection is required"),
  sylobVersion: yup.string().required("Product version is required"),
  sylobMavenVersion: yup
    .string()
    .required("Maven version is required")
    .matches(/^\d+(\.\d+){0,2}(-[A-Za-z0-9.-]+)?$/, "Invalid Maven version format"),
  translationDueDate: yup
    .string()
    .required("Translation due date is required")
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, "Please enter a valid date (DD/MM/YYYY)")
    .test("not-in-past", "Translation due date cannot be in the past", (value) => {
      if (!value) return true;
      const [day, month, year] = value.split("/").map(Number);
      const selectedDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }),
});

const languages = [{ code: "en", name: "English" }];
const products = [
  { code: "5", name: "Fortee" },
  { code: "9", name: "Sylob 9" },
];

const ExportTranslationForm: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTranslationOptionId, setSelectedTranslationOptionId] = useState<number>();
  const extractTranslation = useExportServicePack();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<ExportServicePackTranslationForm>({
    resolver: yupResolver(schema),
    defaultValues: schema.getDefault(),
  });

  const mavenVersion = watch("sylobMavenVersion");

  useEffect(() => {
    const match = mavenVersion?.match(/^(\d+)/);
    setValue("sylobVersion", match?.[1] || "");
  }, [mavenVersion, setValue]);

  const onSubmit = (data: ExportServicePackTranslationForm) => {
    const formData = {
      ...data,
      ...(selectedTranslationOptionId && { referenceTranslationId: selectedTranslationOptionId }),
    };
    extractTranslation.mutate(formData, {
      onSuccess: (translationId) => navigate("/export", { state: { translationId } }),
    });
  };

  const renderButtonGroup = (items: typeof languages | typeof products, field: string, title: string) => (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h2 className="text-xl font-semibold text-gray-700 mb-4">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((item) => (
          <button
            key={item.code}
            type="button"
            className={`w-full py-4 px-4 border rounded-lg transition-all ${
              watch(field as any) === item.code
                ? "bg-blue-100 text-primary border-blue-500 shadow"
                : "bg-white text-primary hover:bg-gray-100 border-gray-300"
            }`}
            onClick={() => setValue(field as any, item.code)}
          >
            {item.name}
          </button>
        ))}
      </div>
      {errors[field as keyof typeof errors] && (
        <p className="text-red-500 text-sm mt-2">{errors[field as keyof typeof errors]?.message}</p>
      )}
    </div>
  );

  const renderInput = (
    name: keyof ExportServicePackTranslationForm,
    label: string,
    placeholder: string,
    readOnly = false
  ) => (
    <div>
      <label className="block mb-2 text-sm font-medium">{label}</label>
      <input
        {...register(name)}
        placeholder={placeholder}
        className={`w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-light ${
          readOnly ? "bg-gray-100" : ""
        }`}
        readOnly={readOnly}
      />
      {errors[name] && <p className="text-red-500 text-sm mt-1">{errors[name]?.message}</p>}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white shadow-lg rounded-xl border border-gray-200">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h1 className="text-2xl font-bold font-display text-pretty text-primary">New Translation</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {renderButtonGroup(languages, "destinationLanguage", "Target Language")}
        {renderButtonGroup(products, "sylobProduct", "Product")}

        <div className="bg-gray-50 p-4 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Version</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {renderInput("sylobMavenVersion", "Technical Version", "e.g. 252.1.0")}
            {renderInput("sylobVersion", "Major Version", "Auto-detected from technical version", true)}
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block mb-2 text-sm font-medium">Due date</label>
          <Controller
            control={control}
            name="translationDueDate"
            render={({ field }) => (
              <DatePicker
                selected={field.value ? new Date(field.value.split("/").reverse().join("-")) : null}
                onChange={(date) => {
                  const formatted = date
                    ? `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(
                        2,
                        "0"
                      )}/${date.getFullYear()}`
                    : "";
                  field.onChange(formatted);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-light"
                minDate={new Date()}
              />
            )}
          />
          {errors.translationDueDate && (
            <p className="text-red-500 text-sm mt-1">{errors.translationDueDate.message}</p>
          )}
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Reference Translation</h2>
          <SelectTranslation value={selectedTranslationOptionId} onChange={setSelectedTranslationOptionId} />
        </div>

        <div className="flex justify-between pt-4">
          <button
            type="button"
            className="font-display p-2 text-white rounded-md flex items-center gap-2 bg-red-500 hover:bg-red-700"
            onClick={() => navigate(-1)}
          >
            <Icon icon="close">Cancel</Icon>
          </button>
          <button
            type="submit"
            className="font-display p-2 text-white rounded-md flex items-center gap-2 bg-primary hover:bg-primary-dark"
          >
            <Icon icon="export" />
            Extract Translation
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExportTranslationForm;
